import { defineStore } from 'pinia'
import api, { setAuthToken, clearAuthToken } from '@/api/axios'

const DEFAULT_AUTH_CHECK_TTL_MS = 60 * 1000
const parsedAuthCheckTtl = Number(process.env.VUE_APP_AUTH_CHECK_TTL_MS)
const AUTH_CHECK_TTL_MS = Number.isFinite(parsedAuthCheckTtl) && parsedAuthCheckTtl > 0
    ? parsedAuthCheckTtl
    : DEFAULT_AUTH_CHECK_TTL_MS

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: localStorage.getItem('token') || null,
        user: null,
        isTokenChecked: false,
        tokenCheckedAt: 0,
        tokenCheckTtlMs: AUTH_CHECK_TTL_MS,
    }),

    getters: {
        isAuthenticated: (state) => !!state.token,
        getToken: (state) => state.token,
    },

    actions: {
        markTokenChecked() {
            this.isTokenChecked = true;
            this.tokenCheckedAt = Date.now();
        },

        resetTokenCheckState() {
            this.isTokenChecked = false;
            this.tokenCheckedAt = 0;
        },

        isTokenCheckFresh() {
            if (!this.isTokenChecked || this.tokenCheckedAt <= 0) {
                return false;
            }
            return (Date.now() - this.tokenCheckedAt) < this.tokenCheckTtlMs;
        },

        setToken(token) {
            this.token = token;
            localStorage.setItem('token', token);
            setAuthToken(token);
            this.resetTokenCheckState();
        },

        async checkToken({ force = false } = {}) {
            if(this.token == null) {
                this.resetTokenCheckState();
                return false;
            }
            if (!force && this.isTokenCheckFresh()) {
                return true;
            }
            let response;
            try {
                response = await api.get('/auth/check');
            } catch (e) {
                this.clearToken();
                return false;
            }
            if (response.status !== 200) {
                this.clearToken();
                return false;
            }
            if (response.data.token) {
                this.setToken(response.data.token);
                this.markTokenChecked();
                return true;
            }
            if (response.data?.valid === true || response.data?.authenticated === true) {
                this.markTokenChecked();
                return true;
            }
            this.clearToken();
            return false;
        },
        clearToken() {
            this.token = null
            localStorage.removeItem('token')
            clearAuthToken()
            this.resetTokenCheckState()
        },

        async login(credentials) {
            const response = await api.post('/auth', credentials);

            if (response.status !== 200) {
                throw new Error('Login failed')
            }
            if (response.data.token) {
                this.setToken(response.data.token)
                this.markTokenChecked()
            }
        },

        async fetchUser() {
            try {
                const response = await api.get('/auth/me')
                this.user = response.data
            } catch (e) {
                this.clearToken()
            }
        },

        logout() {
            this.clearToken()
            this.user = null
        },
    },
})
